import { post } from "@src/appV2/api/api";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { z } from "zod";

const SHFIT_VERIFICATION_REQUEST = "shift-verification-request";

const shiftVerificationRequestAttributesSchema = z.object({
  clockInTime: z.string().datetime(),
  breakStartTime: z.string().datetime().optional(),
  breakEndTime: z.string().datetime().optional(),
  clockOutTime: z.string().datetime(),

  cdph530: z
    .object({
      nursingServiceAssignment: z.string(),
      stationWingUnitFloor: z.string(),
    })
    .optional(),

  reasonForVerification: z.string(),
  signatureImageBase64Url: z.string(),
  workerAcceptedAcknowledgement: z.boolean(),
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createShiftVerificationRequestBodySchema = z.object({
  data: z.object({
    type: z.literal(SHFIT_VERIFICATION_REQUEST),
    attributes: shiftVerificationRequestAttributesSchema,
  }),
});

export const createShiftVerificationResponseBodySchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(SHFIT_VERIFICATION_REQUEST),
    attributes: shiftVerificationRequestAttributesSchema,
  }),
});

export type CreateShiftVerificationRequestAttributes = z.infer<
  typeof shiftVerificationRequestAttributesSchema
>;
export type CreateShiftVerificationRequestBody = z.infer<
  typeof createShiftVerificationRequestBodySchema
>;
export type CreateShiftVerificationResponseBody = z.infer<
  typeof createShiftVerificationResponseBodySchema
>;

export interface UseSendShiftVerificationRequestProps {
  shiftId: string;
  data: CreateShiftVerificationRequestAttributes;
}

export function useSendShiftVerificationRequest(
  options?: Omit<
    UseMutationOptions<
      CreateShiftVerificationResponseBody,
      unknown,
      UseSendShiftVerificationRequestProps
    >,
    "mutationFn"
  >
): UseMutationResult<
  CreateShiftVerificationResponseBody,
  unknown,
  UseSendShiftVerificationRequestProps
> {
  return useMutation({
    mutationFn: async (props: UseSendShiftVerificationRequestProps) => {
      const { shiftId, data } = props;

      const result = await post({
        url: `/shifts/${shiftId}/verification-requests`,
        responseSchema: createShiftVerificationResponseBodySchema,
        data: {
          data: {
            type: SHFIT_VERIFICATION_REQUEST,
            attributes: {
              ...data,
            },
          },
        },
      });
      return result.data;
    },
    ...options,
  });
}
